import { useRef, type FocusEvent, type ReactNode } from 'react';
import { Button, Form } from 'react-bootstrap';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import IconAlert from '@/components/common/IconAlert';

type OperatorValue = 'and' | 'or';

type OperatorToggleProps = {
  value: OperatorValue;
  onChange: (value: OperatorValue) => void;
  disabled?: boolean;
};

export const OperatorToggle = ({
  value,
  onChange,
  disabled = false
}: OperatorToggleProps) => (
  <div className="btn-group btn-group-sm" role="group">
    <Button
      type="button"
      size="sm"
      disabled={disabled}
      variant={value === 'or' ? 'falcon-primary' : 'outline-secondary'}
      onClick={() => onChange('or')}
      className="px-2 py-0 fs--2"
    >
      یا
    </Button>
    <Button
      type="button"
      size="sm"
      disabled={disabled}
      variant={value === 'and' ? 'falcon-primary' : 'outline-secondary'}
      onClick={() => onChange('and')}
      className="px-2 py-0 fs--2"
    >
      و
    </Button>
  </div>
);

type MassiveKeywordLinesInputProps = {
  label?: string;
  helpTooltip?: ReactNode;
  name: string;
  values?: string[];
  setFieldValue: (field: string, value: string[]) => void;
  handleBlur: (e: FocusEvent<HTMLTextAreaElement>) => void;
  touched?: boolean;
  error?: string;
  rows?: number;
  headerEnd?: React.ReactNode;
};

const MassiveKeywordLinesInput = ({
  label,
  helpTooltip,
  name,
  values = [],
  setFieldValue,
  handleBlur,
  touched,
  error,
  rows = 3,
  headerEnd = null
}: MassiveKeywordLinesInputProps) => (
  <div>
    {(label || headerEnd) && (
      <div className="d-flex align-items-center justify-content-between mb-1">
        {label ? (
          <span className="fs--1 text-700">
            {label}
            {helpTooltip && <HelpTooltip html={helpTooltip} />}
          </span>
        ) : (
          <span />
        )}
        {headerEnd}
      </div>
    )}
    <Form.Control
      as="textarea"
      rows={rows}
      name={name}
      isInvalid={touched && !!error}
      onBlur={handleBlur}
      onChange={e => setFieldValue(name, e.target.value.split('\n'))}
      value={values.join('\n')}
      placeholder="هر کلمه در یک خط"
      style={{ resize: 'none', fontSize: '0.8125rem' }}
    />
    {touched && error && (
      <div className="text-danger fs--2 mt-1">{error}</div>
    )}
  </div>
);

type MassiveMainKeywordsInputProps = {
  name: string;
  values: string[];
  setFieldValue: (field: string, value: string[]) => void;
  handleBlur: (e: FocusEvent<HTMLTextAreaElement>) => void;
  limit: number;
  keywordCount: number;
  displayError: string | null;
  helpTooltip?: ReactNode;
  hints?: string[];
};

export const MassiveMainKeywordsInput = ({
  name,
  values,
  setFieldValue,
  handleBlur,
  limit,
  keywordCount,
  displayError,
  helpTooltip,
  hints = []
}: MassiveMainKeywordsInputProps) => {
  const keywordsRef = useRef<HTMLTextAreaElement>(null);
  const isOverLimit = keywordCount > limit;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="fs--1 text-700 d-inline-flex align-items-center gap-1">
          هر کلمه را در یک خط جدید بنویسید
          {helpTooltip && <HelpTooltip html={helpTooltip} />}
        </span>
        <span
          className={`fs--2 ${isOverLimit ? 'text-danger' : 'text-500'}`}
        >
          {keywordCount}/{limit}
        </span>
      </div>
      <Form.Control
        as="textarea"
        ref={keywordsRef}
        rows={5}
        name={name}
        isInvalid={!!displayError}
        onBlur={handleBlur}
        onChange={e => setFieldValue(name, e.target.value.split('\n'))}
        value={values.join('\n')}
        placeholder={'کلمه کلیدی ۱\nکلمه کلیدی ۲\n...'}
        style={{ resize: 'none', fontSize: '0.875rem' }}
      />
      {displayError && (
        <div className="text-danger fs--2 mt-1">{displayError}</div>
      )}
      {hints.map(hint => (
        <IconAlert key={hint} variant="warning" className="mt-2 mb-0 py-2">
          <span className="fs--1">{hint}</span>
        </IconAlert>
      ))}
    </div>
  );
};

export default MassiveKeywordLinesInput;
